<script>
import date from 'locutus/php/datetime/date'
import strtotime from 'locutus/php/datetime/strtotime'
import AdminLayout from '@layouts/admin-main'
import MemberNav from '@components/admin/member-nav'
import PageNavigation from '@components/admin/page-navigation'
import { fetchList, fetchGroupList } from '@apis/admin/member'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'

export default {
  page: {
    title: 'Admin::MemberList',
    meta: [{ name: 'description', content: 'Admin::MemberList' }],
  },
  components: { AdminLayout, MemberNav, PageNavigation },
  data() {
    return {
      loading: false,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      groups: [],
      excel_headers: [
        ['seq', this.$t('member_list.seq')],
        ['user_id', this.$t('member_list.user_id')],
        ['password', this.$t('member_list.password')],
        ['email_address', this.$t('member_list.email_address')],
        ['user_name', this.$t('member_list.user_name')],
        ['is_denied', this.$t('member_list.is_denied')],
        ['is_admin', this.$t('member_list.is_admin')],
        ['regdate', this.$t('member_list.regdate')],
        ['last_login', this.$t('member_list.last_login')],
        ['is_alive', this.$t('member_list.is_alive')],
        ['deleted_date', this.$t('member_list.deleted_date')],
        ['security_level', this.$t('member_list.security_level')],
        [
          'use_statistics_exclude',
          this.$t('member_list.use_statistics_exclude'),
        ],
        [
          'is_security_level_locked',
          this.$t('member_list.is_security_level_locked'),
        ],
        ['inviter_seq', this.$t('member_list.inviter_seq')],
        ['inviter_user_id', this.$t('member_list.inviter_user_id')],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('member_list.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('member_list.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadGroupList()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadGroupList() {
      const { data } = await fetchGroupList()
      this.groups = data
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = {}
      this.search()
    },
    date,
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('member_list.msg_server_overload'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('member_list.member', [date('YmdHis')]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              case 'last_login':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              case 'deleted_date':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
  },
}
</script>

<template>
  <AdminLayout v-loading.fullscreen="loading">
    <MemberNav></MemberNav>

    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                :clearable="false"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['seq', $t('admin.member_seq')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.regdate_more')],
                      ['regdate_less', $t('admin.regdate_less')],
                      ['last_login_more', $t('admin.last_login_more')],
                      ['last_login_less', $t('admin.last_login_less')],
                      ['inviter_user_id', $t('admin.inviter_user_id')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('member_list.statistics_exclude_members') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.is_admin"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('member_list.chief_administrator') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.is_denied"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('member_list.denial_of_service') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.security_level') }}</div></th
            >
            <td>
              <input
                v-model="search_params.security_level"
                type="text"
                :class="[$style.inputTypeText, $style.w40]"
              />
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['last_login', $t('admin.last_login')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin_member_list.member_list') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <ul :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...groups,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.group_seq }"
        :to="{ query: { ...$route.query, group_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>
          {{ title }}
        </a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.member_seq') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.user_id') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.user_name') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.security_level') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.email_address') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div
              ><a href="#">{{ $t('admin_member_list.regdate') }}</a></div
            ></th
          >
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.last_login') }}</a></div
            ></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.inviter_user_id') }}</div></th
          >
          <th scope="col"><div>&nbsp;</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="data in list" :key="data.seq">
          <td :class="$style.tCenter">{{ data._no }}</td>
          <td :class="$style.tCenter">{{ data.seq }}</td>
          <td :class="$style.tCenter">{{ data.user_id }}</td>
          <td :class="$style.tCenter">{{ data.user_name }}</td>
          <td :class="$style.tCenter">{{
            data.security_level || $t('admin_member_list.no_grade')
          }}</td>
          <td>{{ data.email_address }}</td>
          <td :class="$style.tCenter">{{
            date('Y-m-d H:i:s', new Date(data.regdate))
          }}</td>
          <td :class="$style.tCenter">{{
            date('Y-m-d H:i:s', new Date(data.last_login))
          }}</td>
          <td :class="$style.tCenter">
            <router-link
              v-if="data.inviter_seq"
              :to="{
                name: 'admin-member-catalog',
                params: { member_seq: data.inviter_seq },
              }"
            >
              {{ data.inviter_user_id }}
            </router-link>
            <template v-else>{{ $t('admin.no_data') }}</template>
          </td>
          <td :class="$style.tCenter">
            <router-link
              :to="{
                name: 'admin-member-catalog',
                params: { member_seq: data.seq },
              }"
              :class="[$style.button, $style.gray, $style.strong]"
              @click.prevent
              ><span>{{ $t('admin_member_list.detail') }}</span></router-link
            >
            &nbsp;
            <router-link
              :to="{
                name: 'admin-member-list',
                query: { inviter_seq: data.seq },
              }"
              :class="[$style.button, $style.gray, $style.strong]"
              @click.prevent
              ><span>{{ $t('admin.show_inviter_list') }}</span></router-link
            >
          </td>
        </tr>
        <tr v-if="loading">
          <td colspan="10" :class="$style.tCenter">{{
            $t('admin.loading')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
